export type CityOption = { label: string; value: string };

/**
 * Narrows the city list to what has been typed.
 *
 * The city dropdowns are editable so any place can be entered, but PrimeReact
 * only filters via its own search box inside the panel - typing in the field
 * itself leaves the list untouched, so you end up typing twice. Filtering the
 * options here keeps the list in step with the field.
 */
export const filterCityOptions = (
  options: CityOption[],
  typed?: string | null
): CityOption[] => {
  const query = (typed ?? "").trim().toLowerCase();

  if (!query) {
    return options;
  }

  // An exact match means a city is chosen rather than half-typed; keep the
  // whole list so reopening the dropdown can still browse to another one.
  if (options.some((option) => option.value.toLowerCase() === query)) {
    return options;
  }

  const matches = options.filter((option) =>
    option.label.toLowerCase().includes(query)
  );

  // Free text is allowed, so a place that is not in the list must not leave an
  // empty panel behind.
  return matches.length ? matches : options;
};

/**
 * Whether typing in the city field should open the list.
 *
 * PrimeReact does not open an editable dropdown's panel while you type, so the
 * suggestions stay hidden exactly when they are useful. Choosing an option
 * fires the same change event, so an exact match keeps the panel shut - without
 * that, selecting a city would immediately reopen the list over it.
 */
export const shouldOpenCityList = (
  options: CityOption[],
  typed?: string | null
): boolean => {
  const query = (typed ?? "").trim().toLowerCase();

  if (!query) {
    return false;
  }

  return !options.some((option) => option.value.toLowerCase() === query);
};
